import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// A ring of stacked sandbags with a tripod-mounted machine gun poking over
// the front (+Z) wall, ammo box beside the gunner's spot. Two staggered
// courses of bags keep the part count lean while still reading as a ring.
const BAG_A = "#a08d5f";
const BAG_B = "#7a6f4e";
const GUNMETAL = "#4a4d52";
const TIMBER = "#8b6438";
const BRASS = "#b08d3a";

const RING_R = 0.32;
const BAGS_PER_COURSE = 10;

// One course of bags laid tangentially around the ring, staggered per course.
function course(y: number, offset: number, color: string): Part[] {
  const out: Part[] = [];
  for (let i = 0; i < BAGS_PER_COURSE; i++) {
    const ang = ((i + offset) / BAGS_PER_COURSE) * Math.PI * 2;
    out.push({
      geometry: new THREE.BoxGeometry(0.21, 0.095, 0.13), color,
      position: [Math.sin(ang) * RING_R, y, Math.cos(ang) * RING_R],
      rotation: [0, ang, 0],
    });
  }
  return out;
}

// One splayed tripod leg from the mount point down to the floor.
function tripodLeg(ang: number): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.3, 6), color: GUNMETAL,
    position: [Math.sin(ang) * 0.07, 0.13, 0.04 + Math.cos(ang) * 0.07],
    rotation: [Math.cos(ang) * 0.5, 0, -Math.sin(ang) * 0.5],
  };
}

export const machineGunNest = defineModel({
  id: "machine-gun-nest",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  author: "Fable 5",
  name: "Machine-Gun Nest",
  tags: ["prop", "wwi", "military", "defense", "weapon"],
  summary: "A sandbag ring emplacement with a tripod-mounted machine gun.",
  footprint: 0.42,
  parts(): Part[] {
    return [
      ...course(0.048, 0, BAG_A),
      ...course(0.143, 0.5, BAG_B),
      // Tripod mount for the gun.
      tripodLeg(0),
      tripodLeg((2 * Math.PI) / 3),
      tripodLeg((4 * Math.PI) / 3),
      // Receiver, water-cooling jacket and muzzle, poking over the front wall.
      { geometry: new THREE.BoxGeometry(0.06, 0.06, 0.16), color: GUNMETAL, position: [0, 0.28, 0.06] },
      { geometry: new THREE.CylinderGeometry(0.025, 0.025, 0.2, 8), color: GUNMETAL,
        position: [0, 0.29, 0.23], rotation: [Math.PI / 2, 0, 0] },
      { geometry: new THREE.CylinderGeometry(0.01, 0.01, 0.08, 6), color: GUNMETAL,
        position: [0, 0.29, 0.37], rotation: [Math.PI / 2, 0, 0] },
      // Spade grips at the rear of the receiver.
      { geometry: new THREE.BoxGeometry(0.06, 0.045, 0.03), color: TIMBER, position: [0, 0.27, -0.035] },
      // Ammo box beside the tripod, brass belt end on top.
      { geometry: new THREE.BoxGeometry(0.14, 0.09, 0.1), color: TIMBER, position: [0.17, 0.045, -0.05] },
      { geometry: new THREE.BoxGeometry(0.1, 0.015, 0.06), color: BRASS, position: [0.17, 0.097, -0.05] },
    ];
  },
  animations: [],
});
